import { Check } from '@phosphor-icons/react';

interface Step {
  label: string;
  description?: string;
}

interface StepperProps {
  steps: Step[];
  currentStep: number;
  /** Called when a clickable step is clicked. Steps up to `maxClickableStep` are clickable. */
  onStepClick?: (step: number) => void;
  /** Highest step index the user can jump to (defaults to currentStep — only completed + current). */
  maxClickableStep?: number;
}

export default function Stepper({ steps, currentStep, onStepClick, maxClickableStep }: StepperProps) {
  const limit = maxClickableStep ?? currentStep;

  return (
    <div className="flex items-center justify-center gap-0">
      {steps.map((step, idx) => {
        const isCompleted = idx < currentStep;
        const isCurrent = idx === currentStep;
        const isLast = idx === steps.length - 1;
        const isClickable = onStepClick && idx <= limit && idx !== currentStep;

        return (
          <div key={idx} className="flex items-center">
            {/* Step circle + label */}
            <button
              type="button"
              disabled={!isClickable}
              onClick={() => isClickable && onStepClick(idx)}
              className={`flex flex-col items-center ${isClickable ? 'cursor-pointer' : 'cursor-default'}`}
            >
              <div
                className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold transition-colors ${
                  isCompleted
                    ? 'bg-primary text-white'
                    : isCurrent
                    ? 'border-2 border-blue-600 bg-white text-blue-600'
                    : 'border-2 border-gray-300 bg-white text-gray-400'
                } ${isClickable ? 'hover:ring-2 hover:ring-blue-300' : ''}`}
              >
                {isCompleted ? <Check size={16} weight="bold" /> : idx + 1}
              </div>
              <span
                className={`mt-1.5 text-xs font-medium ${
                  isCurrent ? 'text-blue-600' : isCompleted ? 'text-gray-700' : 'text-gray-400'
                } ${isClickable ? 'hover:text-blue-600' : ''}`}
              >
                {step.label}
              </span>
            </button>

            {/* Connector line */}
            {!isLast && (
              <div
                className={`mx-2 h-0.5 w-12 sm:w-20 ${
                  isCompleted ? 'bg-primary' : 'bg-gray-300'
                }`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
